import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { useAuth } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { Logo } from "../components/Logo";
import NoIndex from "../components/NoIndex";

export default function AcceptInvite() {
  const [params] = useSearchParams();
  const token = params.get("token");
  const [invite, setInvite] = useState(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const { login, register } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!token) {
      setNotFound(true);
      return;
    }
    api
      .get(`/auth/invites/${token}`)
      .then(({ data }) => setInvite(data))
      .catch(() => setNotFound(true));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (invite.user_exists) {
        await login(invite.email, password, token);
      } else {
        await register(name, invite.email, password, token, acceptedTerms);
      }
      toast.success(`You joined ${invite.workspace_name}!`);
      navigate("/app");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (notFound) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground" data-testid="invite-not-found">
        This invite link is invalid or has expired.{" "}
        <Link to="/login" className="ml-1 text-primary">
          Go to login
        </Link>
      </div>
    );
  }
  if (!invite) return <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">Loading invite...</div>;

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <NoIndex />
      <div className="w-full max-w-sm">
        <div className="mb-8 flex items-center">
          <Link to="/" data-testid="invite-home-link"><Logo className="h-8" /></Link>
        </div>
        <h1 className="font-heading text-2xl font-bold tracking-tight" data-testid="invite-workspace-name">
          Join {invite.workspace_name}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Invited as <strong>{invite.email}</strong> ({invite.role})
        </p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          {!invite.user_exists && (
            <Input required placeholder="Your name" value={name} onChange={(e) => setName(e.target.value)} data-testid="invite-name-input" />
          )}
          <Input
            type="password"
            required
            placeholder={invite.user_exists ? "Your password" : "Create a password"}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            data-testid="invite-password-input"
          />
          {!invite.user_exists && (
            <label className="flex items-start gap-2.5 text-xs leading-relaxed text-muted-foreground">
              <input
                type="checkbox"
                required
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                data-testid="invite-terms-checkbox"
                className="mt-0.5 h-4 w-4 shrink-0 cursor-pointer rounded border-border accent-primary"
              />
              <span>
                I agree to the{" "}
                <Link to="/terms" target="_blank" className="font-medium text-primary hover:underline">Terms &amp; Conditions</Link>{" "}
                and the{" "}
                <Link to="/privacy" target="_blank" className="font-medium text-primary hover:underline">Privacy Policy</Link>.
              </span>
            </label>
          )}
          <Button type="submit" disabled={loading || (!invite.user_exists && !acceptedTerms)} className="w-full" data-testid="accept-invite-submit-btn">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : invite.user_exists ? "Sign In & Join" : "Create Account & Join"}
          </Button>
        </form>
      </div>
    </div>
  );
}
